import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import HotRankList from "@/components/feature/HotRankList";
import { useSeo } from "@/hooks/useSeo";

const timelineLabels = ["事件起因", "过程梳理", "关键细节", "当事方回应", "后续观察"];

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const index = articles.findIndex((item) => item.id === id);
  const article = index >= 0 ? articles[index] : undefined;

  const related = article
    ? articles.filter((item) => item.categorySlug === article.categorySlug && item.id !== article.id).slice(0, 3)
    : [];

  const prev = index > 0 ? articles[index - 1] : undefined;
  const next = index >= 0 && index < articles.length - 1 ? articles[index + 1] : undefined;
  const category = article ? categories.find((c) => c.slug === article.categorySlug) : undefined;

  useSeo({
    title: article ? `${article.title} | 52吃瓜网` : "内容未找到 | 52吃瓜网",
    description: article
      ? `${article.excerpt}（来源：${article.source}，${article.publishedAt}更新）`
      : "该事件记录暂时不存在，返回首页浏览今日热门汇总。",
    keywords: article
      ? `${article.tags.join(",")},${article.category},52吃瓜网,今日热门`
      : "52吃瓜网,事件记录",
    path: `/article/${id ?? ""}`,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            articleSection: article.category,
            keywords: article.tags.join(","),
            datePublished: article.publishedAt,
            dateModified: article.publishedAt,
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "52吃瓜网编辑部" },
            publisher: { "@type": "Organization", name: "52吃瓜网" },
            mainEntityOfPage: `https://kimchy.cn/article/${article.id}`,
            image: article.cover,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://kimchy.cn/" },
              {
                "@type": "ListItem",
                position: 2,
                name: article.category,
                item: `https://kimchy.cn/category/${article.categorySlug}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://kimchy.cn/article/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <section className="px-4 py-20 text-center md:px-8">
        <p className="font-heading text-5xl font-black text-background-300">404</p>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          没有找到这条事件记录
        </h1>
        <p className="mt-2 text-sm font-label text-foreground-500">
          它可能已经更新或调整，先去热榜看看当下的热门话题吧。
        </p>
        <Link
          to="/hot"
          className="mt-6 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
        >
          查看今日热榜
        </Link>
      </section>
    );
  }

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 py-6 md:px-8 md:py-8">
        <Breadcrumbs
          items={[
            { label: "首页", to: "/" },
            { label: article.category, to: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />
        <div className="mt-5 max-w-4xl">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${article.categorySlug}`}
              className="rounded-full bg-primary-100 px-3 py-1 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-200"
            >
              {article.category}
            </Link>
            {article.isTop ? (
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-label font-medium text-accent-950">
                今日最大瓜
              </span>
            ) : null}
          </div>
          <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-label text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-database-2-line"></i>
              {article.source}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.publishedAt}
            </span>
            <span className="flex items-center gap-1.5 text-primary-600">
              <i className="ri-fire-line"></i>
              热度 {article.heat}万
            </span>
          </div>
        </div>
      </section>

      <section className="px-4 py-8 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <article className="lg:col-span-3">
            <div className="relative h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
              <img
                src={article.cover}
                alt={`${article.title} - 52吃瓜网事件记录配图`}
                title={`${article.title} 52吃瓜网`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <p className="mt-6 rounded-lg border-l-4 border-primary-400 bg-primary-50 px-5 py-4 text-sm leading-relaxed text-primary-900 font-label">
              {article.excerpt}
            </p>

            <div className="mt-8 flex flex-col gap-5">
              {article.content.map((paragraph, i) => (
                <section key={i}>
                  <h2 className="mb-2 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <span className="h-4 w-1 rounded-full bg-primary-500"></span>
                    {timelineLabels[i] ?? `补充说明 ${i + 1}`}
                  </h2>
                  <p className="text-[15px] leading-loose text-foreground-700 font-label">
                    {paragraph}
                  </p>
                </section>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-xs font-label text-foreground-500">相关标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs font-label text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {prev ? (
                <Link
                  to={`/article/${prev.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex items-center gap-1 text-xs font-label text-foreground-400">
                    <i className="ri-arrow-left-line"></i>
                    上一条
                  </span>
                  <span className="mt-1.5 line-clamp-2 block font-heading text-sm font-bold text-foreground-800 transition-colors group-hover:text-primary-700">
                    {prev.title}
                  </span>
                </Link>
              ) : (
                <span />
              )}
              {next ? (
                <Link
                  to={`/article/${next.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 text-right transition-colors hover:border-primary-300"
                >
                  <span className="flex items-center justify-end gap-1 text-xs font-label text-foreground-400">
                    下一条
                    <i className="ri-arrow-right-line"></i>
                  </span>
                  <span className="mt-1.5 line-clamp-2 block font-heading text-sm font-bold text-foreground-800 transition-colors group-hover:text-primary-700">
                    {next.title}
                  </span>
                </Link>
              ) : null}
            </div>
          </article>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-bar-chart-box-line text-primary-600"></i>
                实时热度榜
              </h3>
              <div className="mt-3">
                <HotRankList limit={6} compact />
              </div>
            </div>
            {category ? (
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary-100 text-sm text-secondary-700">
                    <i className={category.icon}></i>
                  </span>
                  同主题事件
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-500 font-label">
                  {category.description}
                </p>
                <Link
                  to={`/category/${category.slug}`}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-label text-primary-700 transition-colors hover:text-primary-800"
                >
                  进入{category.name}分区
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
            ) : null}
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 md:p-5">
              <h3 className="font-heading text-base font-bold text-primary-800">
                补充信息
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-primary-900/80 font-label">
                如果你掌握本文事件的更多细节或当事方最新回应，欢迎提交给我们。
              </p>
              <Link
                to="/contact"
                className="mt-4 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
              >
                提交补充
              </Link>
            </div>
          </aside>
        </div>

        {related.length ? (
          <div className="mt-12 border-t border-background-200 pt-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              相关事件推荐
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        ) : null}
      </section>
    </>
  );
}